/**
 * FIX PROVISORIO — ITEMS OFFLINE REQUEST
 * --------------------------------------
 * Hidrata `itinerary.destination[]` y `item.destination.destination_info` para los
 * items con `source === 'Web Offline Request'`. El backend no está poblando estos
 * campos cuando el itinerario nace de una solicitud offline, y varios componentes
 * (cart-sidebar, summary-reservation, card-itineraries, details-summary-itinerary)
 * los asumen para agrupar los productos por ciudad.
 *
 * Lo que infiere el front, en orden de prioridad:
 *   - cityName → form.destination.city_name (ciudad elegida por el agente) |
 *                 hotelAdditionalInfo[0].city (proveedor) | item.location | ''
 *   - nights   → item.stay.nights | item.room.nights | 0
 *   - adults   → item.paxAdults | item.adults | 0
 *   - childs   → item.paxChildren | item.childrens | 0
 *   - rooms    → 1 (no se puede inferir del item offline; caso normal)
 *
 * BORRAR este helper (y sus callsites) cuando el backend popule
 * `itinerary.destination` y `item.destination.destination_info` para itinerarios
 * con source = "Web Offline Request".
 */

/** `source` que el backend asigna a los ítems cotizados desde una solicitud offline. */
export const OFFLINE_SOURCE = 'Web Offline Request';

/**
 * Idempotente: se puede llamar en cada reconstrucción del carrito sin efectos
 * colaterales (los campos ya hidratados no se tocan y los destinos ya existentes
 * no se duplican).
 *
 * @param itinerary Itinerario a hidratar (se muta in-place).
 * @param items Items a considerar (los que no tengan `destination.iata_code` se ignoran).
 */
export function hydrateOfflineDestinationInfo(itinerary: any, items: any[]): void {
  if (!itinerary || !Array.isArray(items) || items.length === 0) {
    return;
  }

  // 1. Hidratar cada item.destination.destination_info
  items.forEach((item: any) => {
    if (!item) {
      return;
    }

    if (!item.destination || !item.destination.iata_code) {
      return; // sin iata no podemos inferir nada útil
    }

    if (!item.destination.destination_info) {
      const hotelInfo =
        Array.isArray(item.hotelAdditionalInfo) && item.hotelAdditionalInfo.length > 0
          ? item.hotelAdditionalInfo[0]
          : null;

      const cityName =
        hotelInfo?.city ||
        item.form?.destination?.city_name ||
        item.location ||
        '';

      item.destination.destination_info = {
        cityName,
        nights: item.stay?.nights ?? item.room?.nights ?? 0,
        adults: item.paxAdults ?? item.adults ?? 0,
        childs: item.paxChildren ?? item.childrens ?? 0,
        rooms: 1,
        // arrival / departure: si el backend ya los puso a nivel item, los levantamos.
        arrival: item.arrivalDate || '',
        departure: item.departureDate || '',
      };
    }
  });

  // 2. Reconstruir itinerary.destination[] si está vacío o no contiene los iatas
  //    de los items offline.
  const existingIatas = new Set(
    (Array.isArray(itinerary.destination) ? itinerary.destination : [])
      .map((d: any) => d?.iata_code)
      .filter(Boolean)
  );

  const uniqueIatasFromItems = new Set<string>();
  items.forEach((item: any) => {
    const iata = item?.destination?.iata_code;
    if (iata) {
      uniqueIatasFromItems.add(iata);
    }
  });

  const missingIatas = Array.from(uniqueIatasFromItems).filter(i => !existingIatas.has(i));

  if (missingIatas.length === 0) {
    return;
  }

  if (!Array.isArray(itinerary.destination)) {
    itinerary.destination = [];
  }

  missingIatas.forEach((iata: string) => {
    // Buscar un item con ese iata para tomar su cityName inferido
    const itemForIata = items.find((i: any) => i?.destination?.iata_code === iata);
    const info = itemForIata?.destination?.destination_info;

    itinerary.destination.push({
      iata_code: iata,
      destination_info: {
        cityName: info?.cityName || '',
        nights: info?.nights ?? 0,
        adults: info?.adults ?? 0,
        childs: info?.childs ?? 0,
        rooms: info?.rooms ?? 1,
        arrival: info?.arrival || itemForIata?.arrivalDate || '',
        departure: info?.departure || itemForIata?.departureDate || '',
      },
    });
  });
}
